import type { FC, HTMLAttributes, MouseEventHandler, ReactNode } from 'react';

import { ChevronDown } from '@untitledui/icons/ChevronDown';
import { Share04 } from '@untitledui/icons/Share04';
import { useRef, useState } from 'react';
import { Button, Popover } from 'react-aria-components';

import type { User } from '@/utils/session/fetch-user';

import { Badge } from '@/components/base/badges/badges';
import { NavDropdownMenu } from '@/components/navigation/base-components/nav-dropdown-menu';
import { cx, sortCx } from '@/utils/cx';

import type { NavItem } from '../header-navigation';

const styles = sortCx({
  root: 'group relative flex w-full cursor-pointer items-center rounded-md bg-primary outline-focus-ring transition duration-100 ease-linear select-none hover:bg-primary_hover focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2',
  rootSelected: 'bg-active hover:bg-secondary_hover',
});

interface NavItemBaseProps {
  /** Whether the nav item shows only an icon. */
  iconOnly?: boolean;
  /** Whether the collapsible nav item is open. */
  open?: boolean;
  /** URL to navigate to when the nav item is clicked. */
  href?: string;
  /** Icon component to display. */
  icon?: FC<HTMLAttributes<HTMLOrSVGElement>>;
  /** Badge to display. */
  badge?: ReactNode;
  /** Whether the nav item is currently active. */
  current?: boolean;
  /** Whether to truncate the label text. */
  truncate?: boolean;
  /** Handler for click events. */
  onHover?: MouseEventHandler;
  /** Content to display. */
  children?: ReactNode;
  /** List of sub-items to display. */
  items?: (NavItem | { divider: true; label?: string })[];
  /** User data for avatar display. */
  user?: User | null;
}

export const NavDropdownItem = ({
  current,
  badge,
  href,
  icon: Icon,
  children,
  truncate = true,
  items,
  user,
}: NavItemBaseProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const iconElement = Icon && (
    <Icon
      aria-hidden="true"
      className="mr-2 size-5 shrink-0 text-fg-quaternary transition-inherit-all"
    />
  );

  const badgeElement =
    badge && (typeof badge === 'string' || typeof badge === 'number') ? (
      <Badge className="ml-3" color="gray" type="pill-color" size="sm">
        {badge}
      </Badge>
    ) : (
      badge
    );

  const labelElement = (
    <span
      className={cx(
        'flex-1 text-md font-semibold text-secondary transition-inherit-all group-hover:text-secondary_hover',
        truncate && 'truncate',
        current && 'text-secondary_hover',
      )}
    >
      {children}
    </span>
  );

  const isExternal = href && href.startsWith('http');
  const externalIcon = isExternal && (
    <Share04 className="size-4 stroke-[2.5px] text-fg-quaternary" />
  );

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="relative">
      <Button
        ref={triggerRef}
        onPress={handleToggle}
        className={cx('px-3 py-2', styles.root, current && styles.rootSelected)}
        aria-current={current ? 'page' : undefined}
        aria-expanded={isOpen}
      >
        {iconElement}
        {labelElement}
        {externalIcon}
        {badgeElement}
        <ChevronDown
          aria-hidden="true"
          className={cx(
            'ml-3 size-4 shrink-0 stroke-[2.5px] text-fg-quaternary transition-transform',
            isOpen && '-scale-y-100',
          )}
        />
      </Button>

      {items && items.length > 0 && (
        <Popover
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          triggerRef={triggerRef}
          placement="bottom start"
          offset={4}
          className={({ isEntering, isExiting }) =>
            cx(
              'origin-(--trigger-anchor-point) will-change-transform',
              isEntering &&
                'placement-bottom:slide-in-from-top-0.5 duration-150 ease-out animate-in fade-in',
              isExiting &&
                'placement-bottom:slide-out-to-top-0.5 duration-100 ease-in animate-out fade-out',
            )
          }
        >
          <NavDropdownMenu items={items} user={user} />
        </Popover>
      )}
    </div>
  );
};
